"use client";

import { cn } from "@repo/ui/lib/utils";
import { useEffect, useRef } from "react";

// A thin bar along the top edge that fills (right to left) as the reader
// scrolls through the article, like a reader app. Drawn with transform, so
// scrolling never re-renders React.
export function ReadingProgress({ targetId, className }: { targetId: string; className?: string }) {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const target = document.getElementById(targetId);
      const bar = barRef.current;
      if (!target || !bar) return;
      const rect = target.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const progress = total <= 0 ? 1 : Math.min(1, Math.max(0, -rect.top / total));
      bar.style.transform = `scaleX(${progress})`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [targetId]);

  return (
    <div aria-hidden className="pointer-events-none fixed inset-x-0 top-0 z-50 h-1">
      <div ref={barRef} style={{ transform: "scaleX(0)" }} className={cn("h-full origin-right bg-current", className)} />
    </div>
  );
}
